import React from 'react';
import { StandardPage } from 'ui/base/page/standard_page';
import { BackButton } from 'ui/base/button/back_button';
import { ActiveButton } from 'ui/public/button';
import BaseStyles from './delete_account.module.css';
import Messages from './delete_account_messages';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { DeleteAccountStore } from './delete_account_store';
import { LabeledInput, PasswordInput } from 'ui/public/input';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { FormSubmitEvent } from 'ui/base/types';
import { useHistory } from 'react-router-dom';
import { ResultKind } from 'services/base/types';
import { ErrorMessage } from 'ui/public/error_message/error_message';
import { observer } from 'mobx-react';
import { BasePath } from 'services/base/page_path';
import { HistoryService } from 'services/base/history';

type DeleteAccountStyles = {
  headerContainer: string;
  formContainer: string;
  title: string;
  text: string;
  buttonContainer: string;
};

const themedStyles: ThemedStyles<DeleteAccountStyles> = {
  [Theme.DAYLIGHT]: {
    headerContainer: BaseStyles.headerContainer,
    formContainer: BaseStyles.formContainer,
    title: BaseStyles.title,
    text: BaseStyles.daylightText,
    buttonContainer: BaseStyles.buttonContainer,
  },
  [Theme.MIDNIGHT]: {
    headerContainer: BaseStyles.headerContainer,
    formContainer: BaseStyles.formContainer,
    title: BaseStyles.title,
    text: BaseStyles.midnightText,
    buttonContainer: BaseStyles.buttonContainer,
  },
};

type BaseDeleteAccountProps = {
  store: DeleteAccountStore;
};

const BaseDeleteAccount = observer(({ store }: BaseDeleteAccountProps) => {
  const accounts = useAccountsDomain();
  const history = useHistory();
  const handleSave = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSave(accounts));
    if (result.kind === ResultKind.OK) {
      HistoryService.clear(history);
      HistoryService.replace(history, { kind: BasePath.INTRO });
    }
  }, [accounts, history, store]);

  const styles = useStyles(themedStyles);
  return (
    <StandardPage>
      <form onSubmit={handleSave}>
        <div className={styles.headerContainer}>
          <BackButton/>
        </div>
        <div className={styles.formContainer}>
          <div className={styles.title}>
            {Messages.title()}
          </div>
          <div className={styles.text}>
            {Messages.text()}
          </div>
          <LabeledInput label={Messages.password()}>
            <PasswordInput store={store.password}/>
          </LabeledInput>
          <ErrorMessage message={store.passwordError}/>
        </div>
        <div className={styles.buttonContainer}>
          <ActiveButton
            busy={store.busy}
            reserveBusyIconArea={true}
            type='submit'
          >
            {Messages.delete()}
          </ActiveButton>
        </div>
      </form>
    </StandardPage>
  );
});

export const DeleteAccount = () => {
  const [store] = React.useState(new DeleteAccountStore());
  return <BaseDeleteAccount store={store}/>;
};
